/**
 * One suggester answer (16 Oct): the ticket as it is now, the suggested team
 * with its confidence, reasons and person, the other options, and the history
 * it was read from. Read-only; deciding and reassigning are their own cards.
 */

import { Badge, Button, Card, Typography } from "@norican/react";

import { useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type {
  AssignSuggestionResponse,
  SuggestedPerson,
  SuggestedTeam,
  SuggestionReason,
} from "@/lib/api";

import { isPersonReasonCode, isReasonCode, isReassignReason, levelTone } from "./lib";

function useReasonText() {
  const { t } = useT("tickets");
  // Codes and params come from the server (`dict[str, str]`): a safe bag.
  const loose = t as unknown as (key: string, params?: Record<string, string>) => string;
  return (reason: SuggestionReason): string => {
    const params: Record<string, string> = { ...(reason.params ?? {}) };
    const why = params.reason;
    if (why !== undefined && isReassignReason(why)) {
      params.reason = t(`itAdmin.assign.reassign.reason.${why}`);
    }
    if (isReasonCode(reason.code)) {
      return loose(`itAdmin.assign.reason.${reason.code}`, params);
    }
    if (isPersonReasonCode(reason.code)) {
      return loose(`itAdmin.assign.personReason.${reason.code}`, params);
    }
    return reason.code;
  };
}

function PersonLine({ person }: { person: SuggestedPerson }) {
  const text = useReasonText();
  return (
    <div className="flex flex-col">
      <Typography variant="label">
        {person.name}
        {person.email ? (
          <Typography as="span" variant="caption" tone="muted">
            {" "}
            · {person.email}
          </Typography>
        ) : null}
      </Typography>
      <Typography variant="caption" tone="muted">
        {text(person.reason)}
      </Typography>
    </div>
  );
}

function TeamBlock({ team, primary }: { team: SuggestedTeam; primary: boolean }) {
  const { t } = useT("tickets");
  const text = useReasonText();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Typography variant={primary ? "heading-5" : "label"}>{team.team}</Typography>
        <Badge tone={levelTone(team.level)} size="sm">
          {t(`itAdmin.assign.level.${team.level}`)} ·{" "}
          {t("itAdmin.assign.confidence", { pct: String(team.confidence) })}
        </Badge>
        {team.is_current ? (
          <Badge tone="info" variant="outline" size="sm">
            {t("itAdmin.assign.stays")}
          </Badge>
        ) : null}
        {team.open_load == null ? null : (
          <Typography as="span" variant="caption" tone="muted">
            {t("itAdmin.assign.openLoad", { count: String(team.open_load) })}
          </Typography>
        )}
      </div>
      <ul className="ml-4 list-disc">
        {(team.reasons ?? []).map((reason, i) => (
          <li key={`${reason.code}-${String(i)}`}>
            <Typography variant="body-sm">{text(reason)}</Typography>
          </li>
        ))}
      </ul>
      {primary && team.person ? (
        <div className="flex flex-col gap-2">
          <Typography variant="caption" tone="muted">
            {t("itAdmin.assign.assignTo")}
          </Typography>
          <PersonLine person={team.person} />
        </div>
      ) : null}
      {primary && (team.backups ?? []).length > 0 ? (
        <div className="flex flex-col gap-1">
          <Typography variant="caption" tone="muted">
            {t("itAdmin.assign.backups")}
          </Typography>
          <Typography variant="body-sm">
            {(team.backups ?? []).map((p) => p.name).join(", ")}
          </Typography>
        </div>
      ) : null}
    </div>
  );
}

export function SuggestionView({ answer }: { answer: AssignSuggestionResponse }) {
  const { t } = useT("tickets");
  const { formatDateTime } = useDateFormatter();
  const ticket = answer.ticket;
  return (
    <div className="flex flex-col gap-4">
      {ticket ? (
        <Card>
          <Card.Body className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Typography variant="label">
                {ticket.number} · {ticket.subject}
              </Typography>
              {ticket.ivanti_url ? (
                <Button
                  render={<a href={ticket.ivanti_url} target="_blank" rel="noreferrer" />}
                  variant="outline"
                  size="sm"
                >
                  {t("itAdmin.assign.openInIvanti")}
                </Button>
              ) : null}
            </div>
            <Typography variant="body-sm" tone="muted">
              {[ticket.status, ticket.offering, ticket.service, ticket.category, ticket.site]
                .filter((v) => v != null && v !== "")
                .join(" · ")}
            </Typography>
            <Typography variant="body-sm">
              {t("itAdmin.assign.now")}: {t("itAdmin.assign.team")}{" "}
              <strong>{ticket.team ?? t("itAdmin.assign.noTeam")}</strong> ·{" "}
              {t("itAdmin.assign.owner")}{" "}
              <strong>{ticket.owner ?? t("itAdmin.assign.nobody")}</strong>
            </Typography>
          </Card.Body>
        </Card>
      ) : (
        <Typography variant="body-sm" tone="muted">
          {t("itAdmin.assign.textQuery")}
        </Typography>
      )}

      <Card>
        <Card.Body className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Typography variant="caption" tone="muted">
              {t("itAdmin.assign.suggested")}
            </Typography>
            {answer.needs_decision ? (
              <Badge tone="warning" size="sm">
                {t("itAdmin.assign.needsDecision")}
              </Badge>
            ) : null}
            {answer.ai_used ? (
              <Badge tone="accent" variant="outline" size="sm">
                {t("itAdmin.assign.aiUsed")}
              </Badge>
            ) : null}
          </div>
          {answer.suggestion ? (
            <TeamBlock team={answer.suggestion} primary />
          ) : (
            <Typography variant="body-sm">{t("itAdmin.assign.noSuggestion")}</Typography>
          )}
          {(answer.alternatives ?? []).length > 0 ? (
            <div className="border-border flex flex-col gap-3 border-t pt-3">
              <Typography variant="caption" tone="muted">
                {t("itAdmin.assign.alternatives")}
              </Typography>
              {(answer.alternatives ?? []).map((team) => (
                <TeamBlock key={team.team} team={team} primary={false} />
              ))}
            </div>
          ) : null}
          {answer.history ? (
            <div className="border-border flex flex-col gap-1 border-t pt-3">
              <Typography variant="caption" tone="muted">
                {t("itAdmin.assign.historyTitle")} ·{" "}
                {t(`itAdmin.assign.historyBasis.${answer.history.basis}`, {
                  label: answer.history.label,
                })}{" "}
                · {t("itAdmin.assign.historySample", { count: String(answer.history.sample) })}
              </Typography>
              <Typography variant="body-sm">
                {(answer.history.outcomes ?? [])
                  .map((o) => `${o.team} ${String(o.pct)}%`)
                  .join(" · ")}
              </Typography>
            </div>
          ) : null}
          {answer.data_pulled_at ? (
            <Typography variant="caption" tone="muted">
              {t("itAdmin.assign.dataFrom", { when: formatDateTime(answer.data_pulled_at) })}
            </Typography>
          ) : null}
        </Card.Body>
      </Card>
    </div>
  );
}
